// deployment_class: customer_env_allowed
/**
 * The template a developer edits — what exists on the dashboard, and in what order.
 *
 * ══ THE GOVERNING RULE (E3 2026-08-29) ═════════════════════════════════════
 *
 *   "Let's think of our modules, dashboard, and templates as purely containers
 *    to illustrate data."
 *
 *   "Our template is agnostic to any project and any data. It is simply a
 *    container to display data. There is no preconceived, preconfigured pathway
 *    to any previous or known data within the database."
 *
 * Everything below obeys that, and so must anything added later. A template
 * describes SHAPE — which modules exist, which elements each holds, in what
 * order. It never names a project, never queries a table, and never carries a
 * figure. Data arrives at runtime and fills the shape; the shape knows nothing
 * about what arrived.
 *
 * The test for any future change here: if it stops working when the database is
 * empty, it does not belong in this file. A container must be describable with
 * no data in existence at all — that is the whole point of one.
 *
 * This rule is why the earlier Import Data surface was wrong: it fetched the
 * project list and offered specific rows as choices. It worked, and it made the
 * template a view onto particular data rather than a container for any.
 *
 * ── The two modes (E3 2026-08-29) ──────────────────────────────────────────
 * E3: *"In developer mode, we have full access to everything to create, delete,
 * modify, change in any way. In user mode, it's read only… As a developer, I am
 * developing the functionality. Users, if they have any permissions, can just
 * move around for customizations. They don't actually change the functionality
 * of things."*
 *
 * So the line is not permission, it is KIND OF CHANGE:
 *   developer — decides what exists and what it can do (functionality)
 *   user      — arranges what they were given (customisation)
 *
 * This file models the developer's half. A user's own arrangement is a separate
 * thing layered on top and never edits this.
 *
 * ── What the developer can do ──────────────────────────────────────────────
 * E3: *"I want to be able to edit and move things around… either remove it or
 * add it or change the order of things… all of the elements that we currently
 * have in the template."*
 *
 * Every element the bare template renders is therefore addressable: the modules
 * in the rail, each module's eyebrow and title, its tabs, and its summary. Each
 * can be removed, restored and reordered.
 *
 * ── Removal is HIDING, deliberately ────────────────────────────────────────
 * Nothing is destroyed. A removed element is marked hidden and keeps its place
 * in the order, so restoring it puts it back where it was rather than at the
 * end. That is what makes editing safe to experiment with: every removal is
 * reversible, and the template records a decision rather than a deletion.
 *
 * Pure functions over plain data — no clock, no storage, no React. Persistence
 * and the UI are someone else's job, which is what keeps this testable.
 */
import type { ModuleKey } from './dashboard-data';

/**
 * The parts of a module a developer can place.
 *
 * `space` and `flexible-space` are LAYOUT, and they are first-class components
 * rather than a property of something else — exactly as macOS's Customize
 * Toolbar treats Space and Flexible Space. Breathing room is a thing you place,
 * which is why it can be dragged, reordered and removed like anything else.
 */
export type ElementKind =
  | 'eyebrow'
  | 'title'
  | 'tabs'
  | 'summary'
  | 'space'
  | 'flexible-space';

/**
 * Spacers may repeat; content may not.
 *
 * E3: "if they're there and we drag them, it's already there. We can't have
 * doubles." That rule is about CONTENT — two titles in one module is a mistake.
 * Two gaps is a layout decision, and Apple allows exactly that.
 */
export function isRepeatable(kind: ElementKind): boolean {
  return kind === 'space' || kind === 'flexible-space';
}

/**
 * ── What can never be removed (E3 2026-08-29) ──────────────────────────────
 *
 * E3: *"we will never want to get rid of the titles and namings of certain
 * things… Some things should not be removed, like the title and name of a
 * module."* And: *"the executive module never is removed. That's a stay that
 * never gets changed in terms of removal."*
 *
 * The principle underneath both: **structural identity is not editable;
 * arrangement is.** A module with no title cannot be identified — you would be
 * looking at a nameless box. A workspace with no Executive is incoherent,
 * because every other module funnels there
 * ([[project_composition_vocabulary_lock_2026_08_27]]).
 *
 * These are enforced HERE, in the pure state layer, not by omitting a button.
 * A rule that lives only in the UI is a preference: it survives only as long as
 * every render path remembers it, and it can be defeated by a hand-edited
 * stored template. Refusing the operation itself means no path can reach the
 * broken state — the same discipline as the board projection having no edge to
 * traverse rather than a filter that says no.
 */

/** Elements that ARE the module's identity. Never removable. */
export function isPermanentElement(kind: ElementKind): boolean {
  return kind === 'title';
}

/**
 * Modules the workspace cannot be coherent without. Never removable.
 *
 * E3 2026-08-29: *"Projects and executive should always be there no matter what.
 * But oversight, board, and planning can be optional… Maybe not every [agency]
 * has a board, or memberships to the on-site directive."*
 *
 * The two that stay are the two that are not optional in the business: Executive
 * is the hub every other module funnels into, and Projects holds the deals —
 * an agency with no projects is not doing the work the platform exists for.
 * Board, Planning and Oversight describe how a particular agency is organised,
 * and agencies differ, which is exactly what having several templates is for.
 */
export function isPermanentModule(key: ModuleKey): boolean {
  return key === 'executive' || key === 'projects';
}

/** One placed element. Carries an id because spacers are indistinguishable by kind. */
export interface ElementInstance {
  readonly id: string;
  readonly kind: ElementKind;
}

/** The shipped arrangement — what "the default set" means. */
export const DEFAULT_ELEMENTS: readonly ElementKind[] = ['eyebrow', 'title', 'tabs', 'summary'];

export interface ModuleTemplate {
  /** Which module this is. */
  readonly key: ModuleKey;
  /** Removed from the rail — kept in order so restoring returns it to its place. */
  readonly hidden: boolean;
  /**
   * The elements placed in this module, IN ORDER.
   *
   * Order lives here because Apple's model needs it: dragging within a module
   * reorders, and there is nowhere else for that to be recorded. Absence is
   * removal — a removed element is simply not in this list, and it remains
   * available in the package, where every element always lives. That is why
   * removal needs no trash: nothing is destroyed, it is only unplaced.
   */
  readonly elements: readonly ElementInstance[];
  /** The module's tabs, in order. Membership AND order are the developer's. */
  readonly tabs: readonly string[];
  /** Tabs removed but remembered, so they can be restored. */
  readonly hiddenTabs: readonly string[];
}

export interface DashboardTemplate {
  /** What this template is called. Names it in a library of them. */
  readonly name: string;
  /** Every module, in rail order. */
  readonly modules: readonly ModuleTemplate[];
}

/** Build the starting template from the shipped module definitions. */
export function templateFrom(
  order: readonly ModuleKey[],
  tabsOf: (m: ModuleKey) => readonly string[],
  name = 'Kathedral default',
): DashboardTemplate {
  return {
    name,
    modules: order.map((key) => ({
      key,
      hidden: false,
      elements: DEFAULT_ELEMENTS.map((kind) => ({ id: kind, kind })),
      tabs: [...tabsOf(key)],
      hiddenTabs: [],
    })),
  };
}

/**
 * A stable id for a newly placed element.
 *
 * Content kinds are unique within a module, so the kind IS the id. Spacers
 * repeat, so they take the lowest free suffix — derived from what is already
 * placed rather than from a counter, a clock or randomness. That keeps this
 * layer pure and makes the same drag produce the same template every time,
 * which is what lets a stored template be compared and a test be deterministic.
 */
function nextElementId(placed: readonly ElementInstance[], kind: ElementKind): string {
  if (!isRepeatable(kind)) return kind;
  let n = 1;
  while (placed.some((e) => e.id === `${kind}-${n}`)) n += 1;
  return `${kind}-${n}`;
}

function mapModule(
  t: DashboardTemplate,
  key: ModuleKey,
  f: (m: ModuleTemplate) => ModuleTemplate,
): DashboardTemplate {
  return { ...t, modules: t.modules.map((m) => (m.key === key ? f(m) : m)) };
}

/** Move an item within an array. Out-of-range moves are no-ops, never throws. */
function reorder<T>(items: readonly T[], from: number, to: number): readonly T[] {
  if (from === to) return items;
  if (from < 0 || from >= items.length) return items;
  if (to < 0 || to >= items.length) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as T);
  return next;
}

// ── Modules ────────────────────────────────────────────────────────────────

/** Remove a module from the rail. It keeps its position for restoring. */
export function hideModule(t: DashboardTemplate, key: ModuleKey): DashboardTemplate {
  // Refused, not hidden. See isPermanentModule.
  if (isPermanentModule(key)) return t;
  return mapModule(t, key, (m) => ({ ...m, hidden: true }));
}

export function showModule(t: DashboardTemplate, key: ModuleKey): DashboardTemplate {
  return mapModule(t, key, (m) => ({ ...m, hidden: false }));
}

/** Move a module up or down the rail. Hidden modules keep their slot. */
export function moveModule(
  t: DashboardTemplate,
  key: ModuleKey,
  direction: -1 | 1,
): DashboardTemplate {
  const i = t.modules.findIndex((m) => m.key === key);
  if (i === -1) return t;
  return { ...t, modules: reorder(t.modules, i, i + direction) };
}

// ── Elements within a module ───────────────────────────────────────────────

export function hideElement(
  t: DashboardTemplate,
  key: ModuleKey,
  el: ElementKind,
): DashboardTemplate {
  // Refused, not hidden. See isPermanentElement.
  if (isPermanentElement(el)) return t;
  return mapModule(t, key, (m) => ({ ...m, elements: m.elements.filter((e) => e.kind !== el) }));
}

export function showElement(
  t: DashboardTemplate,
  key: ModuleKey,
  el: ElementKind,
): DashboardTemplate {
  return mapModule(t, key, (m) =>
    m.elements.some((e) => e.kind === el) && !isRepeatable(el)
      ? m
      : { ...m, elements: [...m.elements, { id: nextElementId(m.elements, el), kind: el }] },
  );
}

// ── The three drag operations (E3 2026-08-29 · the macOS model) ────────────
//
// E3: *"There are no x's and arrows by the existing elements. There's just the
// importing and exporting of the element."* Customize Toolbar has exactly three
// gestures and no buttons: drag IN from the palette, drag OUT to remove, drag
// WITHIN to reorder. These are those three, and nothing else is needed.

/**
 * Drag IN — place an element at a position.
 *
 * Content may not double ("we can't have doubles"); spacers may, because two
 * gaps is a layout decision rather than a mistake. An out-of-range index clamps
 * instead of throwing: a drop lands somewhere sensible or the editor is unusable.
 */
export function placeElement(
  t: DashboardTemplate,
  key: ModuleKey,
  kind: ElementKind,
  atIndex: number,
): DashboardTemplate {
  return mapModule(t, key, (m) => {
    if (!isRepeatable(kind) && m.elements.some((e) => e.kind === kind)) return m;
    const next = [...m.elements];
    const i = Math.max(0, Math.min(atIndex, next.length));
    next.splice(i, 0, { id: nextElementId(m.elements, kind), kind });
    return { ...m, elements: next };
  });
}

/**
 * Drag OUT — unplace one specific element.
 *
 * By id, not by kind, because two spacers are different elements that look
 * identical; removing "a space" would otherwise remove the wrong one. Permanent
 * elements refuse here exactly as they do in {@link hideElement} — the drag
 * layer cannot become a second route around a rule.
 */
export function unplaceElement(
  t: DashboardTemplate,
  key: ModuleKey,
  id: string,
): DashboardTemplate {
  return mapModule(t, key, (m) => {
    const target = m.elements.find((e) => e.id === id);
    if (!target || isPermanentElement(target.kind)) return m;
    return { ...m, elements: m.elements.filter((e) => e.id !== id) };
  });
}

/**
 * Drag WITHIN — reorder by moving one element to an index.
 *
 * Absolute rather than relative (`moveElement(id, to)` not `up`/`down`) because
 * a drop knows where it landed, not how far it travelled. Out-of-range is a
 * no-op, never a wrap-around.
 */
export function moveElement(
  t: DashboardTemplate,
  key: ModuleKey,
  id: string,
  toIndex: number,
): DashboardTemplate {
  return mapModule(t, key, (m) => {
    const from = m.elements.findIndex((e) => e.id === id);
    if (from === -1) return m;
    return { ...m, elements: reorder(m.elements, from, toIndex) };
  });
}

/** The elements placed in a module, in order — what the module renders. */
export function elementsOf(t: DashboardTemplate, key: ModuleKey): readonly ElementInstance[] {
  return t.modules.find((m) => m.key === key)?.elements ?? [];
}

// ── Tabs ───────────────────────────────────────────────────────────────────

/** Remove a tab. Remembered, so it can be put back. */
export function hideTab(t: DashboardTemplate, key: ModuleKey, tab: string): DashboardTemplate {
  return mapModule(t, key, (m) =>
    m.tabs.includes(tab)
      ? { ...m, tabs: m.tabs.filter((x) => x !== tab), hiddenTabs: [...m.hiddenTabs, tab] }
      : m,
  );
}

/** Put a removed tab back, at the end of the current order. */
export function showTab(t: DashboardTemplate, key: ModuleKey, tab: string): DashboardTemplate {
  return mapModule(t, key, (m) =>
    m.hiddenTabs.includes(tab)
      ? { ...m, tabs: [...m.tabs, tab], hiddenTabs: m.hiddenTabs.filter((x) => x !== tab) }
      : m,
  );
}

export function moveTab(
  t: DashboardTemplate,
  key: ModuleKey,
  tab: string,
  direction: -1 | 1,
): DashboardTemplate {
  return mapModule(t, key, (m) => {
    const i = m.tabs.indexOf(tab);
    if (i === -1) return m;
    return { ...m, tabs: reorder(m.tabs, i, i + direction) };
  });
}

// ── Reading the template ───────────────────────────────────────────────────

/** The modules a reader actually sees, in order. */
export function visibleModules(t: DashboardTemplate): readonly ModuleTemplate[] {
  return t.modules.filter((m) => !m.hidden);
}

/** Is this element part of the module as the template defines it? */
export function elementVisible(
  t: DashboardTemplate,
  key: ModuleKey,
  el: ElementKind,
): boolean {
  const m = t.modules.find((x) => x.key === key);
  return m ? m.elements.some((e) => e.kind === el) : false;
}

/** The tabs of a module, in the developer's order. */
export function tabsOf(t: DashboardTemplate, key: ModuleKey): readonly string[] {
  return t.modules.find((m) => m.key === key)?.tabs ?? [];
}

/** Tabs removed from a module, offered back for restoring. */
export function hiddenTabsOf(t: DashboardTemplate, key: ModuleKey): readonly string[] {
  return t.modules.find((m) => m.key === key)?.hiddenTabs ?? [];
}

/** Rename a template — what "Save as" produces. */
export function renameTemplate(t: DashboardTemplate, name: string): DashboardTemplate {
  const trimmed = name.trim();
  return trimmed === '' ? t : { ...t, name: trimmed };
}
